import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { applyAttribute } from '../../utils/apply-attribute';
import { getRandomId } from '../../utils/get-random-id';
import { renderInputNativeInputChild } from '../../shared/form.util';
import { COMPONENT_TAG } from './radio-button.constants';

/**
 * Radio Buttons allow you to select one of at least two options. Activating a Radio Button therefore excludes the
 * activation of other Radio Buttons in the same group.
 *
 * @slot - Label placed within the element
 * @slot message - `wb-control-error` or `wb-control-hint` to further describe the current state
 */
@Component({
  tag: 'wb-radio-button',
  styleUrl: 'radio-button.scss',
  shadow: true,
})
export class RadioButtonComponent implements ComponentInterface {
  @Element()
  public el: HTMLWbRadioButtonElement;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** Disables the radio. */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** Marks control as invalid. Automatically set by slotted `wb-control-error` */
  @Prop({ reflect: true, mutable: true })
  public error: boolean = false;

  /** Draws the radio in a checked state. */
  @Prop({ mutable: true, reflect: true })
  public checked: boolean = false;

  /** Determines whether the input is required or not */
  @Prop()
  public required?: boolean = false;

  /** The radio's value attribute */
  @Prop()
  public value: string = 'on';

  /** The radio's name attribute */
  @Prop()
  public name!: string;

  /** If content or message is not provided on expected slots, you should provide at least the aria-label to give more information about this field for accessibility reasons */
  @Prop()
  public inputAriaLabel: string;

  /** Emitted when the checked state changes */
  @Event()
  public wbchange: EventEmitter<{ name: string; value: string; checked: boolean }>;

  /** Emitted when the checked state changes */
  @Event({ composed: false })
  public change: EventEmitter<void>;

  /** Emitted when the radio loses focus */
  @Event()
  public wbblur: EventEmitter<void>;

  /** Emitted when the radio receives focus */
  @Event()
  public wbfocus: EventEmitter<void>;

  /** Returns a reference to the native input element */
  @Method()
  public async getInputReference(): Promise<HTMLInputElement> {
    return this.inputEl;
  }

  private TagNames = transformTagNames(this, COMPONENT_TAG, ['wb-control-error', 'wb-control-hint', 'wb-text', 'wb-radio-group', COMPONENT_TAG]);

  private inputEl!: HTMLInputElement;
  private randomInputId: string;
  private randomMessageId: string;

  public connectedCallback(): void {
    this.randomInputId = `input-${getRandomId()}`;
    this.randomMessageId = `${this.randomInputId}-message`;
    this.updateDirection();
  }

  private bindRef = (el) => {
    this.inputEl = el;
  };

  private handleMessageSlotChange = () => {
    const messageEl = this.el.querySelector<HTMLWbControlErrorElement | HTMLWbControlHintElement>(`${this.TagNames.WbControlError},${this.TagNames.WbControlHint}`);

    if (messageEl) {
      messageEl.theme = this.theme;
      messageEl.id = messageEl.id || this.randomMessageId;
      this.inputEl.setAttribute('aria-describedby', messageEl.id);
    } else {
      this.inputEl.removeAttribute('aria-describedby');
    }

    this.error = Boolean(this.el.querySelector(this.TagNames.WbControlError));
  };

  @Watch('checked')
  public onCheckedChange(): void {
    if (this.checked) {
      this.getSiblingRadios().forEach((el) => (el.checked = false));
    }

    this.wbchange.emit(this.getValueChangeEventDetail());
    this.change.emit();
  }

  private handleBlur = () => this.wbblur.emit();

  private handleFocus = () => this.wbfocus.emit();

  private handleChange = (evt: InputEvent) => {
    this.checked = (evt.target as HTMLInputElement).checked;
  };

  private getValueChangeEventDetail(): { name: string; value: string; checked: boolean } {
    return { name: this.name, value: this.value, checked: this.checked };
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection(): void {
    applyAttribute(this.el, 'rtl', SharedDirectionService.isRtl);
  }

  @Listen('keydown')
  public handleKeyDown(event) {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();

      const shouldGoToPreviousItem = ['ArrowUp', 'ArrowLeft'].includes(event.key);
      const activeRadioEls = this.getAllRadios().filter((radio) => !radio.disabled);
      const currentIndex = activeRadioEls.indexOf(this.el);
      const nextIndex = shouldGoToPreviousItem ? currentIndex - 1 : currentIndex + 1;
      const nextIndexWithOverflow = (nextIndex + activeRadioEls.length) % activeRadioEls.length;
      const nextElWithOverflow = activeRadioEls[nextIndexWithOverflow] as HTMLWbRadioButtonElement;

      nextElWithOverflow.checked = true;
      nextElWithOverflow.getInputReference().then((el) => el.focus());
    }
  }

  private getSiblingRadios(): Array<HTMLWbRadioButtonElement> {
    return this.getAllRadios().filter((el) => el !== this.el);
  }

  private getAllRadios(): Array<HTMLWbRadioButtonElement> {
    const radioGroupEl = this.el.closest(this.TagNames.WbRadioGroup);

    if (!radioGroupEl) {
      return [this.el];
    }

    return Array.from(radioGroupEl.querySelectorAll(this.TagNames.WbRadioButton));
  }

  private hasError(): string {
    return this.el.querySelector(`${this.TagNames.WbControlError}[slot="message"]`) != null ? 'true' : 'false';
  }

  // note: tabbing is different from native radio controls
  // reproduction steps: tab to an anchor in a radio control label, then tab back
  // expected: focus is on the checked radio control
  // actual: focus is on the radio control with the anchor element
  public render(): JSX.Element {
    const { WbText } = this.TagNames;

    renderInputNativeInputChild(this.el, {
      type: 'radio',
      name: this.name,
      value: this.value,
      checked: this.checked,
      disabled: this.disabled,
    });

    return (
      <Host class="wb-radio-button" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <label class="wrapper">
          <input //
            ref={this.bindRef}
            type="radio"
            id={this.randomInputId}
            checked={this.checked}
            disabled={this.disabled}
            required={this.required}
            value={this.value}
            name={this.name}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            aria-invalid={this.hasError()}
            aria-label={this.inputAriaLabel}
          />
          <div class="indicator" />
          <WbText size="l" static>
            <slot />
          </WbText>
        </label>
        <div class="message-container" aria-live="polite">
          <slot name="message" onSlotchange={this.handleMessageSlotChange} />
        </div>
      </Host>
    );
  }
}
